import { currencyFormatter } from 'tio-ui/format';
import {
  PhSlidersHorizontal,
  PhArticle,
  PhListChecks,
  PhUserCircle,
  PhMoney,
  PhUsersThree,
  PhPaperclip,
  PhBell,
  PhSparkle,
  PhMagnifyingGlass,
} from 'ember-phosphor-icons';
import { getFieldValue, getFieldValues } from './sections';
import { DEFAULT_HUMAN_AUDIT_PCT, isHumanAuditPctUnset } from '../audit';
import { workflowsFor } from './workflows';
import type { ProgramTypeMeta, SectionId } from './sections';
import type { ComponentLike } from '@glint/template';
import type { TasProgramTemplateExtension as TASProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';

type FieldsSignature = TASProgramTemplateModel['fields'];
type IconComponent = ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
/** Minimal translate fn — the consuming component passes `intl.t`. */
type TranslateFn = (key: string, args?: { count?: number; pct?: number }) => string;

export interface SummaryRow {
  label: string;
  value: string;
}
export interface SummaryGroup {
  title: string;
  icon: IconComponent;
  rows: SummaryRow[];
  /** Section this group summarizes — lets the summary rail jump to it. */
  sectionId: SectionId;
  /** Render rows stacked (label above value) — for long values like approver chains. */
  stacked?: boolean;
}

/**
 * Format an integer-cents value as USD, localized to `locale` (separators follow
 * the viewer's locale; the currency stays USD), or an em dash when empty/invalid.
 */
export function money(value: unknown, locale = 'en-US'): string {
  if (value == null || value === '') return '—';
  const cleaned = String(value).replace(/[^0-9.]/g, '');
  if (!cleaned) return '—';
  const cents = Number.parseFloat(cleaned);
  if (Number.isNaN(cents)) return '—';
  return currencyFormatter(locale).format(cents / 100);
}

interface SummaryInput {
  fields: FieldsSignature;
  meta: ProgramTypeMeta;
  code: string;
  isTrial: boolean;
  /** Translate fn (intl.t) — used to localize group titles, row labels, and values. */
  t: TranslateFn;
  /** Active locale for currency formatting (defaults to en-US when absent). */
  locale?: string;
}

/** SNAKE_CASE enum → Title Case, for approver-role display (e.g. TIO_ANALYZER → "Tio Analyzer"). */
function humanizeEnumName(name: string): string {
  return name
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** One ordered approver in a workflow's `.values` chain. */
interface ApproverEntry {
  role?: string;
  employeeId?: string;
  ordinal?: number;
}

const S = 'program_builder.summary';

/**
 * Builds the live, grouped configuration read-out shown in the summary rail (and
 * reused by Review & Launch). Reads the working-draft fields + model so it
 * updates as the admin edits. Group titles / row labels / value words are i18n
 * keys resolved via the passed-in `t`; data values (amounts, counts, codes,
 * approver-role names) render as-is. Groups gate on program type; rows show real
 * values where set and an em dash otherwise (the rail hides the em-dash rows).
 */
export function buildSummary({
  fields,
  meta,
  code,
  isTrial,
  t,
  locale,
}: SummaryInput): SummaryGroup[] {
  const value = (name: string) => getFieldValue(fields, name);
  const str = (name: string) => String(value(name) ?? '').trim();
  const bool = (name: string) => Boolean(value(name));
  const label = (id: string) => t(`${S}.label.${id}`);
  const val = (id: string) => t(`${S}.value.${id}`);
  const group = (id: string) => t(`${S}.group.${id}`);
  const pct = (name: string) => (str(name) ? `${str(name)}%` : '—');
  const days = (name: string) =>
    str(name) ? t(`${S}.value.days`, { count: Number(str(name)) }) : '—';
  const obj = (name: string): Record<string, unknown> => {
    const raw = value(name);
    return raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  };
  const groups: SummaryGroup[] = [];

  const creditMode = value('LIMIT_CAPACITY') === 'CREDITS' || value('LIMIT_CAPACITY') === true;

  // ---- Basics ----
  const paymentLabel =
    str('PROGRAM_PAYMENT') === 'ON_ATTEND'
      ? val('payment_on_attend')
      : str('PROGRAM_PAYMENT') === 'ON_FULFILLED'
        ? val('payment_on_fulfilled')
        : '—';
  const basicsRows: SummaryRow[] = [
    { label: label('type'), value: meta.key ? meta.label : '—' },
    { label: label('name'), value: str('PROGRAM_NAME') || '—' },
    { label: label('code'), value: code || '—' },
    { label: label('visible'), value: isTrial ? val('draft') : val('yes') },
  ];
  if (!meta.trackingOnly) {
    basicsRows.push({ label: label('payment'), value: paymentLabel });
    // Payment approach only exists on direct-pay-capable programs — row appears
    // once configured, with the Course Cash enum surfaced by its product label.
    const directPay = str('PAYMENT_TYPE_FOR_DIRECT_PAY');
    if (directPay) {
      basicsRows.push({
        label: label('payment_approach'),
        value:
          directPay === 'TAS.AssistanceManner.COURSE_CASH'
            ? // Canonical product name — the same key every surface labels the
              // brand with in plain text (the styled ™ mark stays on rich UI).
              t('common.tas.course_cash.label')
            : val('direct_pay_reimbursement'),
      });
    }
    // Enum-backed timing selects: stored values are bare enum NAMES, humanized
    // the same way the builder's selects label them.
    const enumRow = (id: string, field: string) => {
      if (str(field)) basicsRows.push({ label: label(id), value: humanizeEnumName(str(field)) });
    };
    enumRow('calc_toward', 'CALCULATE_TOWARDS_TOTAL_BASED_ON');
    enumRow('lock_status_on', 'LOCK_EMPLOYEE_STATUS_BASED_ON');
    enumRow('close_on', 'CLOSE_PROGRAM_BASED_ON');
  }
  groups.push({
    title: group('basics'),
    icon: PhSlidersHorizontal,
    sectionId: 'basics',
    rows: basicsRows,
  });

  // ---- Description & disclosures (long text — stacked, never squeezed) ----
  const aboutRows: SummaryRow[] = [];
  if (str('PROGRAM_DESCRIPTION')) {
    aboutRows.push({ label: label('description'), value: str('PROGRAM_DESCRIPTION') });
  }
  if (!meta.trackingOnly) {
    if (str('DIRECT_PAY_DISCLOSURE')) {
      aboutRows.push({
        label: label('direct_pay_disclosure'),
        value: str('DIRECT_PAY_DISCLOSURE'),
      });
    }
    if (str('SUSPENDED_PROGRAM_DISCLOSURE')) {
      aboutRows.push({
        label: label('suspended_disclosure'),
        value: str('SUSPENDED_PROGRAM_DISCLOSURE'),
      });
    }
  }
  if (aboutRows.length) {
    groups.push({
      title: group('about'),
      icon: PhArticle,
      sectionId: 'basics',
      rows: aboutRows,
      stacked: true,
    });
  }

  // ---- Program options ----
  const optionRows: SummaryRow[] = [];
  if (bool('MULTIPLE_INSTANCE_PROGRAM'))
    optionRows.push({ label: label('multiple_instances'), value: val('yes') });
  if (bool('VOUCHER_PROGRAM'))
    optionRows.push({ label: label('voucher'), value: str('VOUCHER_FILENAME') || val('yes') });
  if (optionRows.length) {
    groups.push({
      title: group('program_options'),
      icon: PhListChecks,
      sectionId: 'limits',
      rows: optionRows,
    });
  }

  // ---- Eligibility ----
  const eligibilityRows: SummaryRow[] = [
    { label: label('waiting'), value: days('ELIGIBILITY_WAITING_PERIOD') },
    {
      label: label('unaccredited'),
      value: bool('ALLOW_UNACCREDITED_INSTITUTIONS') ? val('allowed') : val('no'),
    },
    { label: label('dependents'), value: bool('DEPENDENT_PROGRAM') ? val('allowed') : val('no') },
  ];
  if (bool('DEPENDENT_PROGRAM')) {
    // Age limits are keyed by DependentRelationship enum NAMES; a blank entry
    // means "no limit" and stays out of the read-out.
    const ageLimits = Object.entries(obj('DEPENDENT_PROGRAM_AGE_LIMITS'))
      .map(([relationship, age]) => ({ relationship, age: String(age ?? '').trim() }))
      .filter((entry) => entry.age)
      .sort((a, b) => a.relationship.localeCompare(b.relationship));
    for (const entry of ageLimits) {
      eligibilityRows.push({
        label: humanizeEnumName(entry.relationship),
        value: t(`${S}.value.age_years`, { count: Number(entry.age) }),
      });
    }
  }
  groups.push({
    title: group('eligibility'),
    icon: PhUserCircle,
    sectionId: 'eligibility',
    rows: eligibilityRows,
  });

  // ---- Limits & Calculation ----
  if (!meta.trackingOnly) {
    const annualVaries =
      Object.keys(obj('MAXIMUM_ANNUAL_REIMBURSEMENT_BY_EMPLOYMENT_TYPE')).length > 0 ||
      Object.keys(obj('MAXIMUM_ANNUAL_REIMBURSEMENT_BY_FISCAL_YEAR')).length > 0 ||
      Object.keys(obj('MAXIMUM_ANNUAL_REIMBURSEMENT_BY_EMPLOYMENT_TYPE_BY_FISCAL_YEAR')).length > 0;
    const lifetimeVaries =
      Object.keys(obj('MAXIMUM_LIFETIME_REIMBURSEMENT_BY_EMPLOYMENT_TYPE')).length > 0;
    const cap = (field: string, varies: boolean) => {
      if (str(field)) {
        return creditMode
          ? t(`${S}.value.credits`, { count: Number(str(field)) })
          : money(value(field), locale);
      }
      return varies ? val('varies') : '—';
    };

    const byCredit = bool('PRE_CALC_REQUESTED_TOTAL_BY_CREDIT');
    const byPercent = bool('PRE_CALC_REQUESTED_TOTAL_BY_PERCENT');
    const byClaims = bool('CALCULATE_REIMBURSEMENT_BY_CLAIMS_FINANCIALS');
    const byGpa = bool('PRE_CALC_REQUESTED_TOTAL_BY_GPA');
    const calcMethods = [
      byCredit && val('calc_by_credit'),
      byPercent && val('calc_by_percent'),
      byClaims && val('calc_by_claims'),
      byGpa && val('calc_by_gpa'),
    ].filter(Boolean) as string[];

    const limitRows: SummaryRow[] = [
      { label: label('capacity'), value: creditMode ? val('credit_based') : val('dollar_based') },
      { label: label('annual'), value: cap('MAXIMUM_ANNUAL_REIMBURSEMENT', annualVaries) },
      { label: label('lifetime'), value: cap('MAXIMUM_LIFETIME_REIMBURSEMENT', lifetimeVaries) },
    ];
    if (calcMethods.length)
      limitRows.push({ label: label('calculation'), value: calcMethods.join(', ') });
    if (byCredit)
      limitRows.push({
        label: label('rate_per_credit'),
        value: money(value('REIMBURSEMENT_PER_COURSE_CREDIT'), locale),
      });
    if (byPercent)
      limitRows.push({ label: label('reimbursement_pct'), value: pct('REIMBURSEMENT_PERCENTAGE') });
    if (byClaims)
      limitRows.push({
        label: label('claims_pct'),
        value: pct('REIMBURSEMENT_PERCENTAGE_FOR_CLAIMS_FINANCIALS'),
      });
    if (bool('ALLOW_PRE_APPROVAL_SUBMISSION_BEYOND_LIMIT'))
      limitRows.push({ label: label('beyond_limit'), value: val('allowed') });
    if (bool('USE_BALANCE_MULTIPLIER_FOR_PROGRAM_LIMITS'))
      limitRows.push({ label: label('balance_multiplier'), value: val('on') });

    groups.push({ title: group('limits'), icon: PhMoney, sectionId: 'limits', rows: limitRows });
  }

  // ---- Approvals (the ordered approver chains) ----
  const workflowFields = fields as Record<
    string,
    { required?: boolean; values?: unknown[] } | undefined
  >;
  const approvalRows: SummaryRow[] = [];
  for (const workflow of workflowsFor(meta.key)) {
    const config = workflowFields[workflow.workflowField];
    const chain = ((config?.values ?? []) as ApproverEntry[])
      .slice()
      .sort((a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0))
      .map((entry) => (entry.role ? humanizeEnumName(entry.role) : val('approver')))
      .join(' → ');
    if (chain) {
      approvalRows.push({ label: t(workflow.nameKey), value: chain });
    } else if (config?.required) {
      approvalRows.push({ label: t(workflow.nameKey), value: val('required_no_approvers') });
    }
  }
  if (approvalRows.length) {
    groups.push({
      title: group('approvals'),
      icon: PhUsersThree,
      sectionId: 'approvals',
      rows: approvalRows,
      stacked: true,
    });
  }

  // ---- Documents & Evidence ----
  const docCount = (name: string) =>
    getFieldValues(fields, name).filter((entry) => typeof entry === 'string' && entry.trim())
      .length;
  const docRows: SummaryRow[] = [];
  const programDocs = docCount('PROGRAM_APPROVAL_DOCUMENTS_ATTACHMENT_FIELD');
  docRows.push({
    label: label('program_documents'),
    value: programDocs ? String(programDocs) : '—',
  });
  if (meta.usesCourses) {
    const courseDocs = docCount('COURSE_APPROVAL_DOCUMENTS_ATTACHMENT_FIELD');
    const completionDocs = docCount('COMPLETION_DOCUMENTS_ATTACHMENT_FIELD');
    docRows.push({
      label: label('course_documents'),
      value: courseDocs ? String(courseDocs) : '—',
    });
    docRows.push({
      label: label('completion_documents'),
      value: completionDocs ? String(completionDocs) : '—',
    });
  }
  if (!meta.trackingOnly) {
    // A boolean, so "off" is a real answer — an em dash would misread as unset.
    docRows.push({
      label: label('evidence_waiver'),
      value: bool('ALLOW_WAIVER_OF_EVIDENCE_APPROVAL') ? val('allowed') : val('no'),
    });
  }
  groups.push({
    title: group('documents'),
    icon: PhPaperclip,
    sectionId: 'documents',
    rows: docRows,
  });

  // ---- Notifications & Timeline ----
  if (!meta.trackingOnly) {
    const timeRows: SummaryRow[] = [];
    if (meta.usesCourses) {
      timeRows.push({
        label: label('preapproval_lead_max'),
        value: days('PRE_APPROVAL_TO_REGISTRATION_MAX_LEAD_TIME'),
      });
      timeRows.push({
        label: label('preapproval_lead_min'),
        value: days('PRE_APPROVAL_TO_REGISTRATION_MIN_LEAD_TIME'),
      });
      timeRows.push({
        label: label('registration_lag_max'),
        value: days('REGISTRATION_TO_PRE_APPROVAL_MAX_LAG_TIME'),
      });
      timeRows.push({
        label: label('evidence_lag_max'),
        value: days('POST_COURSE_END_EVIDENCE_SUBMISSION_MAX_LAG_TIME'),
      });
      timeRows.push({
        label: label('payment_lag_max'),
        value: days('PAYMENT_SUBMISSION_MAX_DAYS_POST_COMPLETION'),
      });
      timeRows.push({
        label: label('before_year_end_min'),
        value: days('PAYMENT_SUBMISSION_MIN_DAYS_BEFORE_YEAR_END'),
      });
    }
    // Processing expectations apply beyond the course windows: payment-date
    // buffer plus the free-text messaging the classic builder configured.
    timeRows.push({
      label: label('estimated_days_to_paid'),
      value: days('ESTIMATED_DAYS_TO_PAID'),
    });
    timeRows.push({
      label: label('payroll_expectation'),
      value: str('PAYROLL_EXPECTATION') || '—',
    });
    timeRows.push({
      label: label('application_review'),
      value: str('WEEKDAY_FOR_APPLICATION_REVIEW') || '—',
    });
    groups.push({
      title: group('notifications'),
      icon: PhBell,
      sectionId: 'notifications',
      rows: timeRows,
    });
  }

  // ---- Automated decisioning ----
  // Shadow gates everything — an automated share means nothing without it.
  const shadowOn = bool('ENABLE_AGENT_RECOMMENDATIONS');
  const decisionRows: SummaryRow[] = [
    { label: label('shadow_recommendations'), value: shadowOn ? val('on') : val('off') },
  ];
  if (shadowOn) {
    // Iterations cap every analyzer run, shadow included — shadow-level knob.
    decisionRows.push({
      label: label('max_iterations'),
      value: str('ANALYZER_MAXIMUM_ITERATIONS') || '—',
    });
    // Derived, matching the builder.
    const automatedOn = Number(str('ADHERE_TO_RECOMMENDATION_PCT')) > 0;
    decisionRows.push({
      label: label('automated_decisions'),
      value: automatedOn ? val('on') : val('off'),
    });
    if (automatedOn) {
      decisionRows.push({
        label: label('ai_decision_pct'),
        value: pct('ADHERE_TO_RECOMMENDATION_PCT'),
      });
    }
  }
  groups.push({
    title: group('decisioning'),
    icon: PhSparkle,
    sectionId: 'decisioning',
    rows: decisionRows,
  });

  // ---- Audit ----
  // Not gated on shadow: a program can audit TA approvers with no AI decisioning.
  const auditRows: SummaryRow[] = [
    { label: label('audit_base'), value: pct('MARK_FOR_AUDIT_BASE_PCT') },
    {
      label: label('audit_amount_threshold'),
      value: money(value('MARK_FOR_AUDIT_AMOUNT_THRESHOLD_CENTS'), locale),
    },
    { label: label('audit_high_amount_pct'), value: pct('MARK_FOR_AUDIT_HIGH_AMOUNT_PCT') },
  ];
  const humanAuditOn = !bool('DISABLE_HUMAN_DECISION_AUDITS');
  auditRows.push({
    label: label('audit_human_decisions'),
    value: humanAuditOn ? val('on') : val('off'),
  });
  // Its own value (tio-api#9876), inert once audits are off, and an unset one
  // resolves server-side rather than meaning "none" (tio-api#10525).
  if (humanAuditOn) {
    auditRows.push({
      label: label('audit_base_human'),
      value: isHumanAuditPctUnset(value('MARK_FOR_AUDIT_BASE_PCT_HUMAN'))
        ? t(`${S}.value.default_pct`, { pct: DEFAULT_HUMAN_AUDIT_PCT })
        : pct('MARK_FOR_AUDIT_BASE_PCT_HUMAN'),
    });
  }
  groups.push({
    title: group('audit'),
    icon: PhMagnifyingGlass,
    sectionId: 'audit',
    rows: auditRows,
  });

  return groups;
}
